import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import "./AnimatedStats.css";

const defaultStats = [
  {
    value: "6,000+",
    label: "Happy Customers",
    description: "Customers assisted with suitable loan solutions",
    icon: "bi bi-people",
  },
  {
    value: "8,000+",
    label: "Loans Sanctioned",
    description: "Loan applications successfully supported",
    icon: "bi bi-file-earmark-check",
  },
  {
    value: "30+",
    label: "Banking Partners",
    description: "Public and private sector banking relationships",
    icon: "bi bi-bank",
  },
  {
    value: "10+",
    label: "Years of Experience",
    description: "Years of professional financial assistance",
    icon: "bi bi-award",
  },
];

export default function AnimatedStats({ stats }) {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  const items = Array.isArray(stats) && stats.length
    ? stats
    : defaultStats;

  useEffect(() => {
    const currentSection = sectionRef.current;

    if (!currentSection) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -60px 0px",
      }
    );

    observer.observe(currentSection);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="animated-stats-section"
      aria-labelledby="animated-stats-title"
    >
      <div
        className="animated-stats-glow animated-stats-glow-left"
        aria-hidden="true"
      ></div>

      <div
        className="animated-stats-glow animated-stats-glow-right"
        aria-hidden="true"
      ></div>

      <div className="animated-stats-container">
        <div className="animated-stats-header">
          <span className="animated-stats-eyebrow">
            Our Journey in Numbers
          </span>

          <h2 id="animated-stats-title">
            Trusted financial assistance backed by
            <span> proven experience</span>
          </h2>

          <p>
            Monayra combines lender relationships, professional guidance and
            customer-focused service to simplify the loan process.
          </p>
        </div>

        <div className="animated-stats-grid">
          {items.map((stat, index) => (
            <motion.article
              key={`${stat.label}-${index}`}
              className="animated-stat-card"
              initial={{
                opacity: 0,
                y: 30,
                scale: 0.97,
              }}
              animate={
                visible
                  ? {
                      opacity: 1,
                      y: 0,
                      scale: 1,
                    }
                  : {}
              }
              transition={{
                delay: index * 0.12,
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <div className="animated-stat-card-glow"></div>

              <div className="animated-stat-top">
                <span className="animated-stat-icon">
                  <i className={stat.icon || "bi bi-graph-up-arrow"}></i>
                </span>

                <span className="animated-stat-index">
                  {String(index + 1).padStart(2, "0")}
                </span>
              </div>

              <AnimatedNumber
                value={stat.value}
                trigger={visible}
                className="animated-stat-number"
              />

              <h3 className="animated-stat-label">
                {stat.label}
              </h3>

              {stat.description && (
                <p className="animated-stat-description">
                  {stat.description}
                </p>
              )}

              <div className="animated-stat-progress">
                <span></span>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="animated-stats-note">
          <i className="bi bi-info-circle"></i>

          <p>
            Figures represent cumulative business experience and internal
            service records.
          </p>
        </div>
      </div>
    </section>
  );
}

function AnimatedNumber({
  value,
  trigger = false,
  duration = 1600,
  className = "",
}) {
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!trigger) return undefined;

    const valueString = String(value).trim();

    const match = valueString.match(
      /^([\d,.]+)\s*(\+|%|k|m|b)?$/i
    );

    if (!match) {
      setDisplay(valueString);
      return undefined;
    }

    const numericValue = Number(
      match[1].replace(/,/g, "")
    );

    const suffix = match[2] || "";

    if (Number.isNaN(numericValue)) {
      setDisplay(valueString);
      return undefined;
    }

    const decimalPlaces = match[1].includes(".")
      ? match[1].split(".")[1].length
      : 0;

    const startTime = performance.now();
    let animationFrameId;

    const easeOutCubic = (progress) =>
      1 - Math.pow(1 - progress, 3);

    const formatNumber = (number) => {
      if (decimalPlaces > 0) {
        return number.toLocaleString("en-IN", {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        });
      }

      return Math.round(number).toLocaleString("en-IN");
    };

    const animateNumber = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      const currentValue = numericValue * easedProgress;

      setDisplay(`${formatNumber(currentValue)}${suffix}`);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animateNumber);
      } else {
        setDisplay(valueString);
      }
    };

    animationFrameId = requestAnimationFrame(animateNumber);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [trigger, value, duration]);

  return (
    <div className={className} aria-label={String(value)}>
      {display}
    </div>
  );
}